import { isEqual } from "lodash";
import { useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  isEntitySelectionKeptOnSearch,
  lastSearchCriteriaAtomFamily,
  reportSelectedInnerDataIdsByReportIdAF,
  selectedBookingsSelector,
  selectedCellsBookingCalendarAtom,
  selectedCellsBookingsSelector,
  selectedSingleRowRideIdAtom,
  selectedTableRowsIdsAtomFamily,
  selectedTableRowsIncrementAtomFamily,
  selectedTableRowsValues,
  showSelectedOnlyRowsAtomFamily,
  stationsBulkTargetRowsSelector,
  unique,
  type IExtendableDto,
  type ITypeormWhereCriteria,
  type RideEntity,
  type RydeUiUnit,
} from "@ryde/shared";

import { usePageContext } from "../contexts";
import { useBookingsNavigation } from "../navigation";
import { useReportId } from "./reports";
import { useBookingCalendarSelectedIds } from "./use-selected-ids-booking-calender";
import { resolveSelectedRowsIds } from "./use-selected-ids.utils";

export const useSelectedIds = (entityTarget?: RydeUiUnit) => {
  const { entity: entityFromContext } = usePageContext();
  const entity = entityTarget ?? entityFromContext;

  const { isCalendarBookingView } = useBookingsNavigation();
  const { clearSelectedIdsCalender } = useBookingCalendarSelectedIds();
  const setSelectedIds = useSetRecoilState(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const setSelectedSingleRowRideId = useSetRecoilState(
    selectedSingleRowRideIdAtom,
  );
  const setSelectedIncrement = useSetRecoilState(
    selectedTableRowsIncrementAtomFamily(entity),
  );
  const setSelectedBookingCell = useSetRecoilState(
    selectedCellsBookingCalendarAtom,
  );
  const [lastSearchCriteria, setLastSearchCriteria] = useRecoilState(
    lastSearchCriteriaAtomFamily(entity),
  );

  const { reportId } = useReportId();
  const [reportSelectedInnerDataIds, setReportSelectedInnerDataIds] =
    useRecoilState(reportSelectedInnerDataIdsByReportIdAF(reportId));

  const selectedGridIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const selectedRows = useRecoilValue(selectedTableRowsValues(entity));
  const selectedGridBookings = useRecoilValue(selectedBookingsSelector);
  const selectedBookingsCalender = useRecoilValue(
    selectedCellsBookingsSelector,
  );
  const stationBulkTargetRows = useRecoilValue(stationsBulkTargetRowsSelector);

  const isBookingCalendar = entity === "booking" && isCalendarBookingView;

  const selectedRowsValues = useMemo(() => {
    if (isBookingCalendar) {
      return selectedBookingsCalender;
    }

    switch (entity) {
      case "booking":
        return selectedGridBookings;

      case "station":
        return stationBulkTargetRows;

      default:
        return selectedRows;
    }
  }, [
    entity,
    isBookingCalendar,
    selectedBookingsCalender,
    selectedGridBookings,
    selectedRows,
    stationBulkTargetRows,
  ]);

  const selectedRowsIds = useMemo(
    () =>
      resolveSelectedRowsIds({
        entity,
        isBookingCalendar,
        reportSelectedInnerDataIds,
        selectedGridIds,
        selectedBookingsCalender,
      }),
    [
      entity,
      isBookingCalendar,
      reportSelectedInnerDataIds,
      selectedGridIds,
      selectedBookingsCalender,
    ],
  );

  const addSelectedId = useEvent((id: number): void => {
    setSelectedIds(selectedIds => unique([...selectedIds, id]));
  });

  const removeSelectedId = useEvent((id: number): void => {
    setSelectedIds(selectedIds => selectedIds.filter(selId => selId !== id));
  });

  const removeSelectedIds = useEvent((ids: number[]): void => {
    if (!ids.length) {
      return;
    }

    const idsToRemove = new Set(ids);
    setSelectedIds(selectedIds =>
      selectedIds.filter(selId => !idsToRemove.has(selId)),
    );
  });

  const setShowSelectedOnlyRows = useSetRecoilState(
    showSelectedOnlyRowsAtomFamily(entity),
  );

  const clearSelectedIds = useEvent((): void => {
    if (isBookingCalendar) {
      clearSelectedIdsCalender();
    }
    setSelectedIds([]);
    if (entity === "ride") {
      setSelectedSingleRowRideId(undefined);
    }
    setSelectedIncrement(0);
    setReportSelectedInnerDataIds([]);
    setShowSelectedOnlyRows(false);
  });

  const synchronizeSelectedRowsOnSearch = useEvent(
    (
      dtos: IExtendableDto[],
      composedQueryObject: ITypeormWhereCriteria<RideEntity | IExtendableDto>,
    ) => {
      if (isEqual(lastSearchCriteria, composedQueryObject)) {
        if (isEntitySelectionKeptOnSearch(entity)) {
          return;
        }

        setSelectedIds(selIds =>
          selIds.filter(selId => dtos.some(dto => dto.id === selId)),
        );
      } else {
        clearSelectedIds();

        if (entity === "booking") {
          setSelectedBookingCell(new Set());
        }
      }

      setLastSearchCriteria(composedQueryObject);
    },
  );

  return {
    selectedRowsIds,
    selectedRowsValues,
    addSelectedId,
    removeSelectedId,
    removeSelectedIds,
    clearSelectedIds,
    synchronizeSelectedRowsOnSearch,
  };
};
